import angular from 'angular'
import reject from 'lodash/reject'

import { $interval, $q } from '../../ng-compat/imports'
import { SyncManager } from '../sync-manager'

/**
 * Service for triggering expedited (i.e. more frequent) data syncs until a particular condition (usually triggered by
 * a version bump after a background job has completed on the server) is met.
 *
 * @module webregister.managers.expeditedSyncManager
 */

const CHECKIN_PERIOD = 10 * 1000
const DEFAULT_TIMEOUT = 10 * 60 * 1000

/**
 * Service for triggering expedited data syncs.
 *
 * @class ExpeditedSyncManager
 * @constructor
 */
export class ExpeditedSyncManager {
  private _checkinPeriod = 0
  private _expeditedSyncTimer: $TSFixMe
  private _expeditedSyncs: $TSFixMe[] = []

  constructor(public syncManager: SyncManager) {
    'ngInject'
  }

  /**
   * Expedite syncs until the condition defined by the `isSyncComplete` function is met. Expedited syncs are unique by
   * their `isSyncComplete` function (using === comparison).
   *
   * @method expedite
   *
   * @param  {Object} params
   *
   *         @param {Function} params.isSyncComplete
   *                Function to determine whether the expedited sync is complete (i.e. the desired condition has been
   *                met). Must return a truthy value (or a promise for a truthy value).
   *
   *         @param {Number} [params.startTime]
   *                The timestamp that the expedited sync began (defaults to now)
   *
   *         @param {Number} [params.timeout]
   *                The number of milliseconds to timeout after (defaults to 10 minutes)
   *
   * @return {Promise} A promise resolved if the condition is met or rejected if the expedited sync times out
   */
  public expedite({
    isSyncComplete,
    startTime = Date.now(),
    timeout = DEFAULT_TIMEOUT,
  }: {
    isSyncComplete: $TSFixMeFunction
    startTime?: number
    timeout?: number
  }) {
    const deferred = $q.defer()
    const expeditedSync: $TSFixMe = {
      isSyncComplete,
      timeout,
      deferred,
      startTime,
    }

    this._checkExpeditedSync({ expeditedSync }).then(() => {
      // Only queue up the expedited sync if the condition has not already been met
      if (!expeditedSync.syncComplete && !expeditedSync.timedOut) {
        this._addExpeditedSync(expeditedSync)
      }
    })

    return deferred.promise
  }

  // ============================== Private ============================== //

  /**
   * Add a new expedited sync to the queue and trigger the expedited sync mechanism.
   *
   * @private
   * @method _addExpeditedSync
   *
   * @param  {Object} expeditedSync
   *         The expedited sync job (isSyncComplete, startTime, timeout, deferred)
   */
  private _addExpeditedSync(expeditedSync: $TSFixMe) {
    const addedToQueue = this._expeditedSyncs.some(
      (sync: $TSFixMe, index: $TSFixMe) => {
        if (sync.isSyncComplete === expeditedSync.isSyncComplete) {
          this._expeditedSyncs[index] = expeditedSync
          return true
        }
        return false
      }
    )
    if (!addedToQueue) {
      this._expeditedSyncs.push(expeditedSync)
    }

    this._checkinPeriod = 0
    this._scheduleSync()
  }

  /**
   * Inspect all active expedited syncs to determine whether they have completed or expired and schedule the next sync
   * if necessary. Syncs are scheduled with a linear backoff (e.g. 10s, 20s, 30s, etc).
   *
   * @private
   * @method _scheduleSync
   */
  private _scheduleSync() {
    this._checkinPeriod += CHECKIN_PERIOD
    const forceTimeout = this._checkinPeriod > this.syncManager.syncTimeout

    if (this._expeditedSyncTimer) {
      $interval.cancel(this._expeditedSyncTimer)
      this._expeditedSyncTimer = null
    }

    $q.all(
      this._expeditedSyncs.map((expeditedSync: $TSFixMe) =>
        this._checkExpeditedSync({ expeditedSync, forceTimeout })
      )
    ).then(() => {
      this._expeditedSyncs = reject(this._expeditedSyncs, expeditedSync => {
        return expeditedSync.syncComplete || expeditedSync.timedOut
      })

      if (this._expeditedSyncs.length) {
        this._expeditedSyncTimer = $interval(
          () => this._onExpeditedSyncTimer(),
          this._checkinPeriod,
          1
        )
      } else {
        this._expeditedSyncTimer = null
      }
    })
  }

  /**
   * Check a given expedited sync and determine whether it has completed or expired. If it has completed its deferred
   * is resolved, if it is expired its deferred is rejected.
   *
   * @private
   * @method _checkExpeditedSync
   *
   * @param  {Object} params
   *
   *         @param  {Object} expeditedSync
   *                 The expedited sync job (isSyncComplete, startTime, timeout, deferred)
   *
   *         @param  {Boolean} [forceTimeout=false]
   *                 Whether to force a timeout if the expedited sync has not timed out or completed of its own accord
   *
   * @return {Promise} A promise resolved once the expedited sync has been evaluated and updated
   */
  private _checkExpeditedSync({ expeditedSync, forceTimeout }: any) {
    return $q.resolve(expeditedSync.isSyncComplete()).then(syncComplete => {
      expeditedSync.syncComplete = syncComplete

      if (syncComplete) {
        expeditedSync.deferred.resolve()
      } else if (
        forceTimeout ||
        Date.now() - expeditedSync.startTime > expeditedSync.timeout
      ) {
        expeditedSync.timedOut = true
        expeditedSync.deferred.reject()
      }
    })
  }

  /**
   * Triggers a sync via the sync manager when the sync timer is triggered.
   *
   * @private
   * @method _onExpeditedSyncTimer
   */
  private _onExpeditedSyncTimer() {
    this._expeditedSyncTimer = null

    if (this._expeditedSyncs.length) {
      this.syncManager.sync().finally(() => this._scheduleSync())
    }
  }
}

angular
  .module('webregister.managers')
  .service('expeditedSyncManager', ExpeditedSyncManager)
